import { useState } from 'react';
import { Bell, BellOff, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNotifications } from '@/hooks/useNotifications';
import { notificationService } from '@/services/notificationService';
import { useToast } from '@/hooks/use-toast';

export const PushNotificationSettings = () => {
  const { requestPushPermission, pushPermissionGranted, isPushSupported } = useNotifications();
  const [isRequesting, setIsRequesting] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const { toast } = useToast?.() || { toast: (args: any) => console.log(args) };

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      const granted = await requestPushPermission();
      if (granted) {
        // Mostrar notificação de teste
        await notificationService.showNotification({
          id: 'test-notification',
          type: 'reminder',
          title: 'Notificações ativadas!',
          message: 'Agora você receberá notificações importantes do sistema.',
          priority: 'normal',
          isRead: false,
          createdAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleSendTestPush = async () => {
    setIsSendingTest(true);
    try {
      // Dispara push real via servidor de push (proxy /api/notifications -> :5175)
      const res = await fetch('/api/notifications/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payload: {
            title: 'Juris Hub',
            message: 'Notificação de teste enviada com sucesso!',
            id: `test-${Date.now()}`,
            priority: 'normal'
          }
        })
      });

      if (!res.ok) {
        throw new Error(`Falha ao enviar teste (${res.status})`);
      }

      toast({
        title: 'Notificação de teste enviada',
        description: 'Verifique se a notificação apareceu no dispositivo.',
      });
    } catch (error: any) {
      console.error('Erro ao enviar notificação de teste:', error);
      toast({
        title: 'Erro ao enviar teste',
        description: error?.message || 'Não foi possível enviar a notificação de teste.',
        variant: 'destructive'
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  const getPermissionStatus = () => {
    if (!isPushSupported) {
      return {
        status: 'unsupported',
        text: 'Não suportado',
        description: 'Seu navegador não suporta notificações push',
        icon: <AlertCircle className="h-5 w-5 text-red-500" />
      };
    }

    const browserPermission = notificationService.getPermissionStatus();
    
    switch (browserPermission) {
      case 'granted':
        return {
          status: 'granted',
          text: 'Permitido',
          description: 'Notificações push estão ativadas',
          icon: <CheckCircle className="h-5 w-5 text-green-500" />
        };
      case 'denied':
        return {
          status: 'denied',
          text: 'Negado',
          description: 'Permissão negada. Ative nas configurações do navegador',
          icon: <BellOff className="h-5 w-5 text-red-500" />
        };
      default:
        return {
          status: 'default',
          text: 'Não solicitado',
          description: 'Clique no botão abaixo para ativar notificações',
          icon: <Bell className="h-5 w-5 text-yellow-500" />
        };
    }
  };

  const permissionInfo = getPermissionStatus();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notificações Push
        </CardTitle>
        <CardDescription>
          Configure notificações push para receber alertas importantes mesmo quando o app estiver fechado
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status da Permissão */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-3">
            {permissionInfo.icon}
            <div>
              <p className="font-medium text-sm">Status da permissão</p>
              <p className="text-xs text-muted-foreground">{permissionInfo.description}</p>
            </div>
          </div>
          <Badge 
            variant={
              permissionInfo.status === 'granted' ? 'default' :
              permissionInfo.status === 'denied' ? 'destructive' :
              'secondary'
            }
          >
            {permissionInfo.text}
          </Badge>
        </div>

        {/* Tipos de Notificação */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Tipos de notificação que você receberá:</h4>
          <div className="grid gap-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Lembretes de compromissos (24h antes)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span>Tarefas vencidas ou próximas do vencimento</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Pagamentos pendentes (3 dias antes)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span>Alertas urgentes e críticos</span>
            </div>
          </div>
        </div>

        {/* Botão de Ação */}
        {isPushSupported && permissionInfo.status !== 'granted' && (
          <Button
            onClick={handleRequestPermission}
            disabled={isRequesting}
            className="w-full"
          >
            {isRequesting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Solicitando permissão...
              </>
            ) : permissionInfo.status === 'denied' ? (
              <>
                <BellOff className="h-4 w-4 mr-2" />
                Permissão negada
              </>
            ) : (
              <>
                <Bell className="h-4 w-4 mr-2" />
                Ativar notificações push
              </>
            )}
          </Button>
        )}

        {/* Instruções para navegador */}
        {permissionInfo.status === 'denied' && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Como ativar:</strong> Clique no ícone de bloqueio na barra de endereços do navegador 
              e permita notificações para este site.
            </p>
          </div>
        )}

        {/* Botão de Teste quando já está permitido */}
        {isPushSupported && permissionInfo.status === 'granted' && (
          <Button
            onClick={handleSendTestPush}
            disabled={isSendingTest}
            className="w-full"
            variant="default"
          >
            {isSendingTest ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Enviando teste...
              </>
            ) : (
              <>
                <Bell className="h-4 w-4 mr-2" />
                Enviar notificação de teste
              </>
            )}
          </Button>
        )}

        {/* Informação sobre PWA */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Dica:</strong> Para melhor experiência, instale o app como PWA no seu dispositivo. 
            Isso permitirá notificações mesmo quando o navegador estiver fechado.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}; 